import React, { useState, useEffect, useCallback, useMemo } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import axios, { post } from 'axios';
import { Formik } from 'formik';

import { MDBCol, MDBRow } from 'mdbreact';

import useFetch from 'hooks/useFetch';

import FilledButton from 'components/Buttons/FilledButton';
import UnfilledButton from 'components/Buttons/UnfilledButton';

import ContentLoader from 'components/Informations/Loaders/ContentLoader';
import FeaturedText from 'components/FeaturedText';
import Footer from 'components/Footer';
import ReturnPreviousPage from 'components/ReturnPreviousPage';
import Overlay from 'components/Informations/Overlay';
import Toast from 'components/Informations/Toast';

import CreatableField from 'components/FormGroup/CreatableField';
import ErrorMessage from 'components/FormGroup/ErrorMessage';
import DateField from 'components/FormGroup/DateField';
import InputField from 'components/FormGroup/InputField';
import SelectField from 'components/FormGroup/SelectField';
import Switch from 'components/FormGroup/Switch';

import Accordion from 'components/Accordion';
// import ContentFormCard from 'components/Cards/ContentFormCard';

import { formatDate } from 'utils/moment';
// import { searchInArray } from 'utils/core';
import endpoints from 'services/endpoints';
import {
  // phoneNumber,
  onlyNumbers,
  onlyLetters,
  sipNumber,
  minMaxRange,
  // pprNumber,
  // idNumber,
} from 'utils/masks';

import If from 'components/If';
import * as U from './utils';
import * as S from './styles';

const {
  insertEmployee,
  // insertAddress,
  // insertEmployeeContacts,
  // insertEmployeeDocuments,
  getEmployeeFunction,
  getEmployeeLevel,
  getCompanys,
  getCoustCenterOptions,
  getAllEmployeeAllocations,
  getEmployeesToSelect,
} = endpoints;

export default function ManageEmployees({ history, location, user }) {
  const { state } = location;
  const { id_funcionario: idFuncionario, administrador } = user;
  const isAdmin = administrador === 1;

  const [accordionOpen, setAccordionOpen] = useState(false);

  const [overlayVisibility, setOverlayVisibility] = useState(false);
  const [overlayType, setOverlayType] = useState('');
  const [overlayAction, setOverlayAction] = useState(() => {});

  const [toastVisibility, setToastVisibility] = useState(false);
  const [toastType, setToastType] = useState('');
  const [toastMessage, setToastMessage] = useState('');
  const [toastClose, setToastClose] = useState(2000);

  const [zipCode, setZipCode] = useState('');

  const [formAlterations, setFormAlterations] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [directManagers, setDirectManagers] = useState([]);
  const [employeeFunctions, setEmployeeFunctions] = useState([]);
  const [employeeLevels, setEmployeeLevels] = useState([]);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [coustCenterOpts, setCoustCenterOpts] = useState([]);
  const [employeeAllocations, setEmployeeAllocations] = useState([]);
  const [newSelectValue, setNewSelectValue] = useState();
  const [selectedCoustCenter, setSelectedCoustCenter] = useState(null);

  const [hasAppointment, setHasAppointment] = useState(false);
  const [workInAdminHours, setWorkInAdminHours] = useState(false);

  const [countries] = useState([
    { label: 'Argentina', value: 'Argentina' },
    { label: 'Brasil', value: 'Brasil' },
    { label: 'Chile', value: 'Chile' },
    { label: 'Colômbia', value: 'Colômbia' },
    { label: 'México', value: 'México' },
  ]);

  const [coustCentersList] = useFetch(getCoustCenterOptions);
  const [employeesList] = useFetch(getEmployeesToSelect);
  const [getEmployeeFunctions] = useFetch(getEmployeeFunction);
  const [getEmployeeLevels] = useFetch(getEmployeeLevel);
  const [getAllCompanys] = useFetch(getCompanys);
  const [getEmployeeAllocations] = useFetch(
    state
      ? `${getAllEmployeeAllocations}/${state.id_funcionario}`
      : getAllEmployeeAllocations,
  );

  useEffect(() => {
    if (state) {
      (async () => {
        const response = await getEmployeeAllocations;

        const orderedResponse = _.orderBy(response, 'dt_final_alocacao', [
          'desc',
        ]);

        setEmployeeAllocations(orderedResponse);
      })();
    }
  }, [state, getEmployeeAllocations]);

  useEffect(() => {
    const findEmployeesByFunctionName = (functionName, variantName) =>
      employeesList
        .filter(({ nome_funcao }) => {
          const name = nome_funcao.toLowerCase();
          return name.includes(functionName) || name.includes(variantName);
        })
        ?.map(employee => ({
          value: employee.id_funcionario,
          label: employee.nome,
        }));

    const allManagers = findEmployeesByFunctionName('gerente', 'manager');
    const allCoordinators = findEmployeesByFunctionName('coordenador');
    const allPOs = findEmployeesByFunctionName('po');
    const allTechLeads = findEmployeesByFunctionName('lead');
    const allDirectors = findEmployeesByFunctionName('diretor');
    const allScrumMasters = findEmployeesByFunctionName('scrum');

    setDirectManagers([
      ...allManagers,
      ...allCoordinators,
      ...allTechLeads,
      ...allDirectors,
      ...allPOs,
      ...allScrumMasters,
    ]);
  }, [employeesList]);

  useEffect(() => {
    (async () => {
      const levels = await getEmployeeLevels;
      const company = await getAllCompanys;
      const functions = await getEmployeeFunctions;
      const coustCenter = await coustCentersList;

      setEmployeeFunctions(functions);
      
      setEmployeeLevels(
        levels.map(level => ({
          value: level.id_nivel,
          label: level.senioridade,
        })),
      );

      setCompanyOptions(
        company.data?.map(companyEmployee => ({
          value: companyEmployee.id_empresa,
          label: companyEmployee.nome,
        })),
      );

      setCoustCenterOpts(
        coustCenter.map(item => ({ label: item.label, value: item.id })),
      );

      setSelectedCoustCenter(state?.id_centro_de_custo ?? '');
      setHasAppointment(!!state?.bate_ponto || false);
    })();
  }, [
    getEmployeeLevels,
    getAllCompanys,
    getEmployeeFunctions,
    coustCentersList,
    state,
  ]);

  const initialValues = U.getInitialValues(state);

  const renderToast = (type, message, closeTime) => {
    const condition = closeTime !== undefined ? closeTime : toastClose;

    setToastVisibility(true);
    setToastMessage(message);
    setToastType(type);
    setToastClose(condition);

    setTimeout(() => {
      setToastVisibility(false);
      setToastMessage('');
      setToastType('');
    }, condition + 1500);
  };

  const getEmployeeAddress = async setFieldValue => {
    setIsLoading(true);

    try {
      const response = await axios.get(
        `https://viacep.com.br/ws/${zipCode}/json/`,
      );

      if (response.data.erro) {
        setIsLoading(false);
        return renderToast(
          'error',
          'CEP não encontrado. Por favor tente novamente',
          8000,
        );
      }

      const { logradouro, bairro, localidade, uf } = response.data;

      setFieldValue('place', logradouro);
      setFieldValue('neighborhood', bairro);
      setFieldValue('city', localidade);
      setFieldValue('state', uf);
      setIsLoading(false);
    } catch {
      setIsLoading(false);
      return renderToast(
        'error',
        'CEP não encontrado. Por favor tente novamente',
        8000,
      );
    }
  };

  const renderOverlay = type => {
    switch (type) {
      case 'addEmployee':
        return (
          <Overlay
            isOpen={overlayVisibility}
            type="success"
            text={[
              'Você está ',
              <span>
                {state
                  ? 'editando um funcionário'
                  : 'adicionando um novo funcionário'}
              </span>,
              '. Deseja prosseguir?',
            ]}
          >
            <UnfilledButton
              text="Sim"
              handleClick={() => {
                setOverlayType('');
                setOverlayVisibility(false);
                overlayAction();
              }}
            />
            <FilledButton
              text="Não"
              buttonClass="confirm"
              handleClick={() => {
                setOverlayType('');
                setOverlayVisibility(!overlayVisibility);
              }}
            />
          </Overlay>
        );

      case 'cancel':
        return (
          <Overlay
            isOpen={overlayVisibility}
            type="warning"
            text={[
              'Você está ',
              <span>cancelando</span>,
              ' a inserção de um novo funcionário e esta ação irá ',
              <span>descartar</span>,
              ' o funcionário não inserida. Deseja prosseguir?',
            ]}
          >
            <UnfilledButton
              text="Sim, prosseguir"
              handleClick={() => {
                setOverlayVisibility(false);
                setOverlayType('');
                history.push('/auth/employees');
              }}
            />
            <FilledButton
              text="Não"
              buttonClass="warn"
              handleClick={() => {
                setOverlayVisibility(!overlayVisibility);
                setOverlayType('');
              }}
            />
          </Overlay>
        );

      case 'returning':
        return (
          <Overlay
            isOpen={overlayVisibility}
            type="warning"
            text={[
              'Você está ',
              <span>voltando para a tela anterior</span>,
              ' e esta ação irá ',
              <span>descartar</span>,
              ' as vendas que não foram salvas. Deseja continuar?',
            ]}
          >
            <UnfilledButton
              text="Sim, continuar"
              handleClick={() => history.push('/auth/employees')}
            />
            <FilledButton
              text="Não"
              buttonClass="warn"
              handleClick={() => setOverlayVisibility(!overlayVisibility)}
            />
          </Overlay>
        );

      default:
        return '';
    }
  };

  const submitForm = async values => {
    const finalresponse = response => {
      const { status } = response;
      if (status === 200) {
        renderToast(
          'success',
          !state
            ? 'Semântico cadastrado com sucesso'
            : 'Dados alterados com sucesso',
          4000,
        );
        setTimeout(() => history.push('/auth/employees'), 4000);
      }
    };
    const errorInsertEmployee = () =>
      renderToast(
        'error',
        !state
          ? 'Erro ao inserir o funcionário'
          : 'Erro ao alterar dados do funcionário',
        4000,
      );

    const postData = data => {
      post(insertEmployee, data).then(finalresponse).catch(errorInsertEmployee);
    };

    const sendData = {
      id_usr_in: idFuncionario,
      id_usr_up: idFuncionario,
      email: values.email,
      matricula: values.registrationNumber,
      nome: values.fullName,
      dt_nascimento: values.birthDate,
      dt_admissao: values.admissionDate,
      dt_demissao: values.resignationDate,
      id_centro_de_custo: values.coustCenter,
      id_funcao: values.employeeFunction,
      id_nivel: values.seniorLevel,
      id_empresa: values.employeeCompany,
      id_status_funcionario: 1,
      cep: values.zipCode,
      uf: values.state,
      pais: values.country,
      cidade: values.city,
      bairro: values.neighborhood,
      logradouro: values.place,
      numero: values.houseNumber,
      complemento: values.houseComplement,
      pis: { documento: values.sipNumber, id_tipo_documento: 3 },
      step: values.step,
      bate_ponto: values.ahgoraAppointment,
      id_gestor_direto: values.directManager,
      dt_inicio_batidas_artificiais: values.artificialAppointmentsDate,
    };

    if (hasAppointment) {
      delete sendData.dt_inicio_batidas_artificiais;
    }

    if (state) {
      const { id_funcionario: func } = state;
      delete sendData.id_usr_in;
      return postData({ ...sendData, id_funcionario: func });
    }

    return postData(sendData);
  };

  const renderSelectCoustCenter = useCallback(
    ({ setFieldValue, values: val }) =>
      !!coustCenterOpts.length && (
        <SelectField
          hasLabel
          hasLabelMargin
          labelText="Centro de custos (*)"
          placeholderText="Selecione a localização..."
          name="coustCenter"
          options={coustCenterOpts}
          value={coustCenterOpts.find(opt => opt.value === val.coustCenter)}
          handleChange={({ value }) => {
            setFieldValue('coustCenter', value);
            setFormAlterations(true);
            setSelectedCoustCenter(value);
          }}
          isDisabled={!isAdmin}
        />
      ),
    [coustCenterOpts, isAdmin],
  );

  const employeeFunctionOptions = useMemo(() => {
    const findCoustCenter = coustCentersList.find(
      coustCenter => coustCenter.id === selectedCoustCenter,
    );

    if (!findCoustCenter?.id) return [];

    const allFunctions = employeeFunctions.filter(employeeFunction => {
      const coustCenters = employeeFunction?.centros_de_custo?.map(
        coustCenter => coustCenter[1],
      );

      if (!coustCenters || !coustCenters?.length > 0) return null;

      if (!coustCenters?.includes(findCoustCenter.id)) return null;

      return employeeFunction;
    });

    return allFunctions.map(employeeFunction => ({
      label: employeeFunction.nome_funcao,
      value: employeeFunction.id_funcao,
    }));
  }, [selectedCoustCenter, coustCentersList, employeeFunctions]);

  return (
    <>
      {overlayVisibility && renderOverlay(overlayType, overlayAction)}

      {toastVisibility && (
        <Toast
          type={toastType}
          message={toastMessage}
          autoClose={toastClose}
          hideProgress
        />
      )}

      {isLoading && (
        <S.Loader>
          <ContentLoader />
        </S.Loader>
      )}

      <S.Container>
        <MDBRow>
          <MDBCol lg="12">
            <ReturnPreviousPage
              text="Voltar para gerência de funcionários"
              handleClick={() => {
                const haveAlterations = () => {
                  setOverlayVisibility(true);
                  setOverlayType('returning');
                };

                const dontHaveAlterations = () => {
                  setToastVisibility(false);
                  history.push('/auth/employees');
                };

                return formAlterations
                  ? haveAlterations()
                  : dontHaveAlterations();
              }}
            />
            <S.Introduction>
              <S.FeaturedText>
                {state ? 'Edição de funcionário' : 'Cadastro de Funcionário'}
              </S.FeaturedText>
              <FeaturedText
                text={
                  state
                    ? `${state.nome}`
                    : 'Realize o cadastro do novo Semântico'
                }
              />
            </S.Introduction>
          </MDBCol>
        </MDBRow>
        <MDBRow>
          <MDBCol lg="6" sm="12">
            <Formik
              initialValues={initialValues}
              enableReinitialize
              validateOnBlur={false}
              validateOnChange={false}
              validationSchema={U.validationSchema}
              onSubmit={e => submitForm(e)}
            >
              {({
                values,
                errors,
                handleSubmit,
                setFieldValue,
                isSubmitting,
              }) => (
                <S.Form onSubmit={e => submitForm(e, values)} noValidate>
                  <S.Divider>Dados profissionais</S.Divider>
                  <InputField
                    labelText="Número de matrícula (*)"
                    placeholderText="Insira o número de matrícula"
                    name="registrationNumber"
                    value={values.registrationNumber}
                    width={50}
                    handleChange={e => {
                      setFieldValue(
                        'registrationNumber',
                        onlyNumbers(e.target.value),
                      );
                      setFormAlterations(true);
                    }}
                    isDisabled={!isAdmin}
                  />
                  {errors.registrationNumber && (
                    <ErrorMessage message={errors.registrationNumber} />
                  )}

                  <InputField
                    labelText="Número do PIS (*)"
                    placeholderText="Insira o número do PIS"
                    name="sipNumber"
                    value={values.sipNumber}
                    width={50}
                    handleChange={e => {
                      return setFieldValue(
                        'sipNumber',
                        sipNumber(e.target.value),
                      );
                    }}
                    isDisabled={!isAdmin}
                  />
                  {errors.sipNumber && (
                    <ErrorMessage message={errors.sipNumber} />
                  )}

                  {/* <InputField
                    labelText="Cargo exercido (*)"
                    placeholderText="Insira o cargo exercido"
                    name="profession"
                    value={values.profession}
                    handleChange={e => {
                      setFieldValue('profession', e.target.value);
                      setFormAlterations(true);
                    }}
                  />
                  {errors.profession
                  && <ErrorMessage message={errors.profession} />} */}

                  <SelectField
                    hasLabel
                    hasLabelMargin
                    labelText="Empresa(*)"
                    placeholderText="Selecione a empresa..."
                    name="employeeCompany"
                    options={companyOptions}
                    value={companyOptions?.find(
                      option => option.value === values.employeeCompany,
                    )}
                    handleChange={({ value }) => {
                      setFieldValue('employeeCompany', value);
                      setFormAlterations(true);
                    }}
                    isDisabled={!isAdmin}
                  />
                  {errors.employeeCompany && (
                    <ErrorMessage message={errors.employeeCompany} />
                  )}

                  {renderSelectCoustCenter({ setFieldValue, errors, values })}
                  {errors.coustCenter && (
                    <ErrorMessage message={errors.coustCenter} />
                  )}

                  <SelectField
                    hasLabel
                    hasLabelMargin
                    labelText="Função do funcionário(*)"
                    placeholderText="Selecione uma função..."
                    name="employeeFunction"
                    options={employeeFunctionOptions}
                    value={employeeFunctionOptions?.find(
                      option => option.value === values.employeeFunction,
                    )}
                    handleChange={({ value }) => {
                      setFieldValue('employeeFunction', value);
                      setFormAlterations(true);
                    }}
                    isDisabled={!isAdmin || !values.coustCenter}
                  />
                  {errors.seniorLevel && (
                    <ErrorMessage message={errors.seniorLevel} />
                  )}

                  <S.FlexRow>
                    <SelectField
                      hasLabel
                      hasLabelMargin
                      labelText="Nível de senioridade (*)"
                      placeholderText="Selecione um nível..."
                      name="seniorLevel"
                      options={employeeLevels}
                      value={employeeLevels.find(
                        option => option.value === values.seniorLevel,
                      )}
                      handleChange={({ value }) => {
                        setFieldValue('seniorLevel', value);
                        setFormAlterations(true);
                      }}
                      isDisabled={!isAdmin}
                    />
                    {errors.seniorLevel && (
                      <ErrorMessage message={errors.seniorLevel} />
                    )}

                    <InputField
                      labelText="Step (*)"
                      placeholderText="Insira um step de 1 à 5"
                      name="step"
                      value={values.step}
                      handleChange={e => {
                        setFieldValue(
                          'step',
                          minMaxRange('', 5, e.target.value),
                        );
                        setFormAlterations(true);
                      }}
                      isDisabled={!isAdmin}
                    />
                    {errors.step && <ErrorMessage message={errors.step} />}
                  </S.FlexRow>

                  {/* <SelectField hasLabel
                    labelText="Escolaridade (*)"
                    placeholderText="Selecione uma opção..."
                    name="academicLevel"
                    options={U.academicLevelOptions}
                    value={
                    U.academicLevelOptions
                      .find(option => option.value === values.academicLevel)
                  }
                    handleChange={({ value }) => {
                      setFieldValue('academicLevel', value);
                      setFormAlterations(true);
                    }}
                  />

                  {errors.academicLevel
                  && <ErrorMessage message={errors.academicLevel} />} */}

                  <DateField
                    labelText="Data de admissão (*)"
                    name="admissionDate"
                    placeholderText="Insira uma data"
                    onlyMonth={false}
                    selectedValue={values.admissionDate || new Date()}
                    handleChange={e => {
                      setFieldValue('admissionDate', e);
                      setFormAlterations(true);
                    }}
                    disabled={!isAdmin}
                  />
                  {errors.admissionDate && (
                    <ErrorMessage message={errors.admissionDate} />
                  )}

                  <DateField
                    labelText="Data de demissão"
                    name="resignationDate"
                    placeholderText="Insira uma data"
                    onlyMonth={false}
                    selectedValue={'' || values.resignationDate}
                    handleChange={e => {
                      setFieldValue('resignationDate', e);
                      setFormAlterations(true);
                    }}
                    disabled={!isAdmin}
                  />
                  {errors.resignationDate && (
                    <ErrorMessage message={errors.resignationDate} />
                  )}

                  <SelectField
                    hasLabel
                    hasLabelMargin
                    isClearable
                    labelText="Gestor direto"
                    placeholderText="Selecione um gestor..."
                    name="directManager"
                    options={directManagers}
                    value={directManagers.find(
                      option => option.value === values.directManager,
                    )}
                    handleChange={e => {
                      setFieldValue('directManager', e?.value ?? e);
                      setFormAlterations(true);
                    }}
                    isDisabled={!isAdmin}
                  />
                  {errors.directManager && (
                    <ErrorMessage message={errors.directManager} />
                  )}

                  <S.FlexRow>
                    <Switch
                      id="hasAppointment"
                      onChange={() => {
                        const actualValue = hasAppointment;
                        setFormAlterations(true);
                        setHasAppointment(current => !current);
                        setFieldValue('ahgoraAppointment', !actualValue);
                      }}
                      isChecked={hasAppointment}
                      labelText="Apontamento no Ahgora"
                    />

                    <Switch
                      id="adminHours"
                      onChange={() => {
                        const actualValue = workInAdminHours;
                        setFormAlterations(true);
                        setWorkInAdminHours(current => !current);
                        setFieldValue('adminHours', !actualValue);
                      }}
                      isChecked={workInAdminHours}
                      labelText="Trabalha em horário administrativo"
                    />
                  </S.FlexRow>

                  <If condition={!hasAppointment}>
                    <DateField
                      labelText="Não bate ponto desde (*)"
                      name="artificialAppointmentsDate"
                      placeholderText="Insira uma data"
                      onlyMonth={false}
                      selectedValue={
                        values.artificialAppointmentsDate || new Date()
                      }
                      handleChange={e => {
                        setFieldValue('artificialAppointmentsDate', e);
                        setFormAlterations(true);
                      }}
                      disabled={!isAdmin}
                    />
                    {errors.artificialAppointmentsDate && (
                      <ErrorMessage
                        message={errors.artificialAppointmentsDate}
                      />
                    )}
                  </If>

                  <S.Divider>Dados pessoais</S.Divider>
                  <InputField
                    labelText="Nome completo do funcionário (*)"
                    placeholderText="Qual o nome do funcionário?"
                    name="fullName"
                    value={values.fullName}
                    handleChange={e => {
                      setFieldValue('fullName', onlyLetters(e.target.value));
                      setFormAlterations(true);
                    }}
                    pattern="/^[a-zA-Z\s]*$/"
                    isDisabled={!isAdmin}
                  />
                  {errors.fullName && (
                    <ErrorMessage message={errors.fullName} />
                  )}

                  {/* <InputField
                    labelText="Nome completo do mãe"
                    placeholderText="E o nome da sua mãe?"
                    name="motherName"
                    value={values.motherName}
                    handleChange={e => {
                      setFieldValue('motherName', onlyLetters(e.target.value));
                      setFormAlterations(true);
                    }}
                  />
                  {errors.motherName
                  && <ErrorMessage message={errors.motherName} />}

                  <InputField
                    labelText="Nome completo da pai"
                    placeholderText="E do pai?"
                    name="fatherName"
                    value={values.fatherName}
                    handleChange={e => {
                      setFieldValue('fatherName', onlyLetters(e.target.value));
                      setFormAlterations(true);
                    }}
                  />
                  {errors.fatherName
                  && <ErrorMessage message={errors.fatherName} />}

                  <InputField
                    labelText="Nacionalidade (*)"
                    placeholderText="Insira a nacionalidade"
                    name="nacionality"
                    value={values.nacionality}
                    width={50}
                    handleChange={e => {
                      setFieldValue('nacionality', onlyLetters(e.target.value));
                      setFormAlterations(true);
                    }}
                  />
                  {errors.nacionality
                  && <ErrorMessage message={errors.nacionality} />} */}

                  <DateField
                    name="birthDate"
                    placeholderText="Insira a data"
                    onlyMonth={false}
                    selectedValue={values.birthDate || new Date()}
                    handleChange={e => {
                      setFieldValue('birthDate', e);
                      setFormAlterations(true);
                    }}
                    labelText="Data de nascimento (*)"
                    disabled={!isAdmin}
                  />
                  {errors.birthDate && (
                    <ErrorMessage message={errors.birthDate} />
                  )}

                  {/* <SelectField hasLabel
                    labelText="Gênero sexual (*)"
                    placeholderText="Selecione um gênero..."
                    name="sexualGender"
                    options={U.sexualityOptions}
                    value={
                    U.sexualityOptions
                      .find(option => option.value === values.sexualGender)
                  }
                    handleChange={({ value }) => {
                      setFieldValue('sexualGender', value);
                      setFormAlterations(true);
                    }}
                  />
                  {errors.sexualGender
                  && <ErrorMessage message={errors.sexualGender} />}

                  <SelectField hasLabel
                    labelText="Estado civil (*)"
                    placeholderText="Selecione o estado civil..."
                    name="maritalStatus"
                    options={U.matitalStatusOptions}
                    value={
                    U.matitalStatusOptions
                      .find(option => option.value === values.maritalStatus)
                  }
                    handleChange={({ value }) => {
                      setFieldValue('maritalStatus', value);
                      setFormAlterations(true);
                    }}
                  />
                  {errors.maritalStatus
                  && <ErrorMessage message={errors.maritalStatus} />}

                  <InputField
                    labelText="Número do RG (*)"
                    placeholderText="Insira o número do RG"
                    name="idNumber"
                    value={values.idNumber}
                    width={50}
                    handleChange={e => {
                      setFieldValue('idNumber', idNumber(e.target.value));
                      setFormAlterations(true);
                    }}
                  />
                  {errors.idNumber
                  && <ErrorMessage message={errors.idNumber} />}

                  <InputField
                    labelText="Número do CPF (*)"
                    placeholderText="Insira o número do CPF"
                    name="pprNumber"
                    value={values.pprNumber}
                    width={50}
                    handleChange={e => {
                      setFieldValue('pprNumber', pprNumber(e.target.value));
                      setFormAlterations(true);
                    }}
                  />
                  {errors.pprNumber
                  && <ErrorMessage message={errors.pprNumber} />} */}

                  <S.Divider>Dados de contato</S.Divider>
                  <InputField
                    labelText="E-mail (*)"
                    placeholderText="Insira o endereço de e-mail"
                    name="email"
                    value={values.email}
                    handleChange={e => {
                      setFieldValue('email', e.target.value);
                      setFormAlterations(true);
                    }}
                    isDisabled={!isAdmin}
                  />
                  {errors.email && <ErrorMessage message={errors.email} />}

                  {/* <InputField
                    labelText="Telefone celular (*)"
                    placeholderText="Número do telefone celular"
                    name="cellphoneNumber"
                    value={values.cellphoneNumber}
                    width={50}
                    handleChange={e => {
                      setFieldValue('cellphoneNumber', phoneNumber(e.target.value));
                      setFormAlterations(true);
                    }}
                  />
                  {errors.cellphoneNumber
                  && <ErrorMessage message={errors.cellphoneNumber} />}

                  <InputField
                    labelText="Telefone residencial"
                    placeholderText="Número do telefone residencial"
                    name="residenceNumber"
                    value={values.residenceNumber}
                    width={50}
                    handleChange={e => {
                      setFieldValue('residenceNumber', phoneNumber(e.target.value));
                      setFormAlterations(true);
                    }}
                  />
                  {errors.residenceNumber
                  && <ErrorMessage message={errors.residenceNumber} />}
                  <InputField
                    labelText="Telefone comercial"
                    placeholderText="Número do telefone comercial"
                    name="commercialNumber"
                    value={values.commercialNumber}
                    width={50}
                    handleChange={e => {
                      setFieldValue('commercialNumber', phoneNumber(e.target.value));
                      setFormAlterations(true);
                    }}
                  />
                  {errors.commercialNumber
                  && <ErrorMessage message={errors.commercialNumber} />} */}

                  <S.Divider>Dados residenciais</S.Divider>

                  <CreatableField
                    isClearable
                    placeholderText="Selecione o pais..."
                    labelText="País (*)"
                    handleChange={prop => {
                      setFormAlterations(true);
                      setFieldValue(
                        'country',
                        prop ? prop.value : newSelectValue.value,
                      );
                    }}
                    handleInputChange={prop => setNewSelectValue(prop)}
                    value={{ label: values.country, value: values.country }}
                    inputName="country"
                    options={countries}
                    isDisabled={!isAdmin}
                  />
                  {errors.country && <ErrorMessage message={errors.country} />}

                  <InputField
                    labelText="CEP"
                    placeholderText="Insira o CEP"
                    name="zipCode"
                    value={values.zipCode}
                    width={50}
                    onBlur={() => {
                      return values.country && values.country === 'Brasil'
                        ? getEmployeeAddress(setFieldValue)
                        : false;
                    }}
                    handleChange={e => {
                      setFieldValue('zipCode', e.target.value);
                      setFormAlterations(true);
                      setZipCode(e.target.value);
                    }}
                    isDisabled={!isAdmin}
                  />
                  {errors.zipCode && <ErrorMessage message={errors.zipCode} />}

                  <InputField
                    labelText="Logradouro"
                    placeholderText="Insira o nome da rua"
                    name="place"
                    value={values.place}
                    handleChange={e => {
                      setFieldValue('place', e.target.value);
                      setFormAlterations(true);
                    }}
                    isDisabled={!isAdmin}
                  />
                  {errors.place && <ErrorMessage message={errors.place} />}

                  <InputField
                    labelText="Número da residência"
                    placeholderText="Defina o número da residência"
                    name="houseNumber"
                    value={values.houseNumber}
                    width={50}
                    handleChange={e => {
                      setFieldValue('houseNumber', e.target.value);
                      setFormAlterations(true);
                    }}
                    isDisabled={!isAdmin}
                  />
                  {errors.houseNumber && (
                    <ErrorMessage message={errors.houseNumber} />
                  )}

                  <InputField
                    labelText="Complemento"
                    placeholderText="Se houver, defina o complemento"
                    name="complemento"
                    value={values.houseComplement}
                    width={50}
                    handleChange={e => {
                      setFieldValue('houseComplement', e.target.value);
                      setFormAlterations(true);
                    }}
                    isDisabled={!isAdmin}
                  />
                  {errors.houseComplement && (
                    <ErrorMessage message={errors.houseComplement} />
                  )}

                  <InputField
                    labelText="Bairro"
                    placeholderText="Insira o bairro do funcionário"
                    name="neighborhood"
                    value={values.neighborhood}
                    handleChange={e => {
                      setFieldValue('neighborhood', e.target.value);
                      setFormAlterations(true);
                    }}
                    isDisabled={!isAdmin}
                  />
                  {errors.neighborhood && (
                    <ErrorMessage message={errors.neighborhood} />
                  )}

                  <InputField
                    labelText="Cidade"
                    placeholderText="Qual a cidade do funcionário?"
                    name="city"
                    value={values.city}
                    handleChange={e => {
                      setFieldValue('city', e.target.value);
                      setFormAlterations(true);
                    }}
                    isDisabled={!isAdmin}
                  />
                  {errors.city && <ErrorMessage message={errors.city} />}

                  <InputField
                    labelText="Estado"
                    placeholderText="E o estado?"
                    name="state"
                    value={values.state}
                    width={50}
                    handleChange={e => {
                      setFieldValue('state', e.target.value);
                      setFormAlterations(true);
                    }}
                    isDisabled={!isAdmin}
                  />
                  {errors.state && <ErrorMessage message={errors.state} />}

                  <S.Buttons>
                    <FilledButton
                      buttonClass="cancel"
                      text="Cancelar"
                      handleClick={() => {
                        setOverlayVisibility(true);
                        setOverlayType('cancel');
                      }}
                      disabled={!isAdmin}
                    />
                    <FilledButton
                      buttonClass="confirm"
                      text="Salvar"
                      disabled={!isAdmin || !formAlterations || isSubmitting}
                      handleClick={() => {
                        setOverlayVisibility(true);
                        setOverlayType('addEmployee');
                        setOverlayAction(() => handleSubmit);
                      }}
                    />
                  </S.Buttons>
                </S.Form>
              )}
            </Formik>
          </MDBCol>
        </MDBRow>
        {state && (
          <MDBRow>
            <MDBCol lg="6" sm="12">
              <S.Divider>
                Abaixo estão todas as alocações deste funcionário
              </S.Divider>
              <Accordion
                headText="Alocações atuais"
                callbackText="Este funcionário não está alocado em nenhum projeto"
                accordionOpen={accordionOpen}
                setAccordionOpen={setAccordionOpen}
              >
                {employeeAllocations.map(allocation => (
                  <S.Allocation
                    className={
                      new Date(allocation.dt_final_alocacao).getTime() <=
                      new Date().getTime()
                        ? 'isOld'
                        : ''
                    }
                  >
                    <div>
                      <p>{allocation.projeto.toUpperCase()}</p>
                      <p>
                        <span>Início da alocação:</span>
                        {formatDate(
                          allocation.dt_inicio_alocacao,
                          'DD/MM/YYYY',
                        )}
                      </p>
                      <p>
                        <span>Término da alocação:</span>
                        {formatDate(allocation.dt_final_alocacao, 'DD/MM/YYYY')}
                      </p>
                      <p>
                        <span>Percentual de alocação:</span>
                        {`${allocation.percentual}%`}
                      </p>
                    </div>
                  </S.Allocation>
                ))}
              </Accordion>
            </MDBCol>
          </MDBRow>
        )}
      </S.Container>
      <Footer />
    </>
  );
}

ManageEmployees.defaultProps = {
  location: {},
  user: {},
};

ManageEmployees.propTypes = {
  history: PropTypes.shape(
    PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
  ).isRequired,
  user: PropTypes.shape(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  ),
  location: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ),
};
